/**
 * Offline Progress Calculator
 * Pure calculation module for computing what buddies/haulers would have
 * earned while the player was away.
 *
 * Models the real buddy→inventory→hauler pipeline:
 * - Buddies catch fish into inventory (respecting capacity)
 * - Haulers load fish from inventory, travel, sell, return
 * - Inventory is the bottleneck/buffer between the two
 */

import { PlayerState, FishRarity, RarityWeights, Quality } from '@/types';
import { BALANCE_CONFIG } from '@/data';
import { FISH_DEFINITIONS } from '@/data';
import { getZoneDefinition } from '@/data';

/**
 * Report of what happened while the player was offline
 */
export interface OfflineReport {
  offlineDurationMs: number;
  fishCaught: number;
  fishToInventory: number;
  fishSold: number;
  goldEarned: number;
  buddyCatches: number;
  haulerTrips: number;
  xpEarned: number;
}

/** Maximum offline time to simulate (8 hours) */
const MAX_OFFLINE_MS = 8 * 60 * 60 * 1000;

/** Minimum offline time to trigger calculation (5 minutes) */
export const MIN_OFFLINE_MS = 5 * 60 * 1000;

/** Average quality multiplier for rough value estimates */
const AVG_QUALITY_MULTIPLIER = 1.15;

/** XP per fish caught (simplified offline estimate) */
const XP_PER_FISH = 10;

/**
 * Calculate the rarity-weighted average base value of fish in a zone's pool.
 * Uses the same RarityWeights as the FishingEngine's species roller
 * so the estimate matches in-game distribution.
 */
function getWeightedAverageFishValue(zoneId: string): number {
  try {
    const zone = getZoneDefinition(zoneId);
    const poolFish = FISH_DEFINITIONS.filter((f) =>
      zone.fishPool.includes(f.id)
    );
    if (poolFish.length === 0) return 10; // fallback

    let totalWeightedValue = 0;
    let totalWeight = 0;

    for (const fish of poolFish) {
      const weight = RarityWeights[fish.rarity as FishRarity] ?? 0;
      totalWeightedValue += fish.baseValue * weight;
      totalWeight += weight;
    }

    if (totalWeight === 0) return 10; // fallback
    return totalWeightedValue / totalWeight;
  } catch {
    return 10; // fallback if zone not found
  }
}

/**
 * Create an empty report with the given duration.
 */
function createEmptyReport(durationMs: number): OfflineReport {
  return {
    offlineDurationMs: durationMs,
    fishCaught: 0,
    fishToInventory: 0,
    fishSold: 0,
    goldEarned: 0,
    buddyCatches: 0,
    haulerTrips: 0,
    xpEarned: 0,
  };
}

/**
 * Calculate what buddies and haulers would have earned during offline time.
 *
 * Models the real game pipeline:
 * - Buddies catch fish → inventory (stops when full)
 * - Haulers load from inventory → travel → sell → return (clears inventory)
 * - Inventory capacity is the buffer/bottleneck
 *
 * This is a pure function — it does NOT modify game state.
 */
/**
 * Distribute a fish count across zone species weighted by rarity.
 * Returns stacks of COMMON quality fish proportional to how often
 * each species would be caught in-game.
 */
export function distributeOfflineFish(
  count: number,
  zoneId: string
): { definitionId: string; quality: Quality; count: number }[] {
  if (count <= 0) return [];

  try {
    const zone = getZoneDefinition(zoneId);
    const poolFish = FISH_DEFINITIONS.filter((f) =>
      zone.fishPool.includes(f.id)
    );
    if (poolFish.length === 0) return [];

    const totalWeight = poolFish.reduce(
      (sum, f) => sum + (RarityWeights[f.rarity as FishRarity] ?? 0),
      0
    );
    if (totalWeight === 0) return [];

    const stacks: { definitionId: string; quality: Quality; count: number }[] = [];
    let remaining = count;

    for (const fish of poolFish) {
      const proportion = (RarityWeights[fish.rarity as FishRarity] ?? 0) / totalWeight;
      const fishCount = Math.floor(count * proportion);
      if (fishCount > 0) {
        stacks.push({ definitionId: fish.id, quality: Quality.COMMON, count: fishCount });
        remaining -= fishCount;
      }
    }

    // Distribute rounding remainder to most common species first
    for (let i = 0; i < stacks.length && remaining > 0; i++) {
      stacks[i].count++;
      remaining--;
    }

    // If all proportions rounded to 0, give all fish to the most common species
    if (stacks.length === 0 && remaining > 0) {
      stacks.push({ definitionId: poolFish[0].id, quality: Quality.COMMON, count: remaining });
    }

    return stacks;
  } catch {
    return [];
  }
}

export function calculateOfflineProgress(
  elapsedMs: number,
  playerState: PlayerState
): OfflineReport {
  // Cap offline time
  const cappedMs = Math.min(elapsedMs, MAX_OFFLINE_MS);

  // Below minimum threshold — no offline progress
  if (cappedMs < MIN_OFFLINE_MS) {
    return createEmptyReport(cappedMs);
  }

  // --- Fishing Buddies ---
  const hiredEnabledBuddies = playerState.fishingBuddies.filter(
    (b) => b.hired && b.enabled
  );

  if (hiredEnabledBuddies.length === 0) {
    return createEmptyReport(cappedMs);
  }

  // Buddy catch rate (fish per ms)
  const catchIntervalMs =
    BALANCE_CONFIG.FISHING_BUDDY_INTERVAL *
    1000 *
    BALANCE_CONFIG.FISHING_BUDDY_FISHING_TIME_MULT;

  const catchChance =
    BALANCE_CONFIG.BASE_CATCH_CHANCE - BALANCE_CONFIG.FISHING_BUDDY_CATCH_REDUCTION;

  const buddyCatchRate =
    (hiredEnabledBuddies.length * catchChance) / catchIntervalMs;

  // Total catch attempts (for reporting)
  const attemptsPerBuddy = Math.floor(cappedMs / catchIntervalMs);
  const totalAttempts = attemptsPerBuddy * hiredEnabledBuddies.length;

  // Available inventory space
  const inventorySpace = Math.max(
    0,
    playerState.inventory.capacity - playerState.inventory.currentCount
  );

  // Rarity-weighted average fish value
  const avgBaseValue = getWeightedAverageFishValue(playerState.currentZone);
  const avgFishValue = avgBaseValue * AVG_QUALITY_MULTIPLIER;

  // --- Hauler Buddies ---
  const hiredEnabledHaulers = playerState.haulerBuddies.filter(
    (h) => h.hired && h.enabled
  );

  let totalFishCaught = 0;
  let fishToInventory = 0;
  let fishSold = 0;
  let haulerTrips = 0;

  if (hiredEnabledHaulers.length === 0) {
    // --- No haulers: fish fill inventory up to capacity, then buddies stop ---
    const maxCatches = Math.floor(buddyCatchRate * cappedMs);
    fishToInventory = Math.min(maxCatches, inventorySpace);
    totalFishCaught = fishToInventory;
  } else {
    // --- With haulers: model the buddy→inventory→hauler pipeline ---
    const tripTimeMs =
      (BALANCE_CONFIG.HAULER_BASE_TRAVEL_TIME +
        BALANCE_CONFIG.HAULER_LOADING_TIME +
        BALANCE_CONFIG.HAULER_SELLING_TIME) *
      1000;

    // Total hauler capacity per trip cycle
    let totalHaulerCapacityPerTrip = 0;
    for (const hauler of hiredEnabledHaulers) {
      const capacity =
        BALANCE_CONFIG.HAULER_BASE_CAPACITY +
        Math.floor(
          hauler.progression.stats.physique *
            BALANCE_CONFIG.HAULER_CAPACITY_PER_PHYSIQUE
        );
      totalHaulerCapacityPerTrip += capacity;
    }

    // Hauler sell rate (fish per ms, when inventory has fish)
    const haulerSellRate = totalHaulerCapacityPerTrip / tripTimeMs;

    if (buddyCatchRate <= haulerSellRate) {
      // Haulers clear faster than buddies produce — all fish get sold
      // Inventory never fills up, buddies run at full rate the entire time
      totalFishCaught = Math.floor(buddyCatchRate * cappedMs);
      fishSold = totalFishCaught;
      fishToInventory = 0;

      // Haulers only make as many trips as needed for the fish caught
      haulerTrips = Math.ceil(totalFishCaught / totalHaulerCapacityPerTrip);
    } else {
      // Buddies produce faster than haulers can clear
      // Phase 1: inventory fills up (net fill rate = buddy - hauler)
      // Phase 2: inventory full, throughput limited to hauler rate
      const netFillRate = buddyCatchRate - haulerSellRate;

      if (inventorySpace > 0 && netFillRate > 0) {
        const fillTimeMs = Math.min(inventorySpace / netFillRate, cappedMs);

        const phase1Caught = Math.floor(buddyCatchRate * fillTimeMs);
        const phase1Sold = Math.floor(haulerSellRate * fillTimeMs);

        const remainingMs = cappedMs - fillTimeMs;
        const phase2Processed = Math.floor(haulerSellRate * remainingMs);

        totalFishCaught = phase1Caught + phase2Processed;
        fishSold = phase1Sold + phase2Processed;
        fishToInventory = Math.min(inventorySpace, totalFishCaught - fishSold);
      } else {
        // Inventory already full — throughput = hauler rate only
        totalFishCaught = Math.floor(haulerSellRate * cappedMs);
        fishSold = totalFishCaught;
        fishToInventory = 0;
      }

      haulerTrips = Math.floor(cappedMs / tripTimeMs) * hiredEnabledHaulers.length;
    }
  }

  // --- Gold ---
  const goldEarned = Math.floor(fishSold * avgFishValue);

  // --- XP ---
  const fishingXP = totalFishCaught * XP_PER_FISH;
  const haulerXP = Math.floor(goldEarned * BALANCE_CONFIG.HAULER_XP_PER_GOLD);
  const totalXP = fishingXP + haulerXP;

  return {
    offlineDurationMs: cappedMs,
    fishCaught: totalFishCaught,
    fishToInventory,
    fishSold,
    goldEarned,
    buddyCatches: totalAttempts,
    haulerTrips,
    xpEarned: totalXP,
  };
}
